import { TransactionListItem } from "@common/schemas/transaction.schema";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import classNames from "classnames";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useOverlays } from "contexts/OverlayContext";
import { useIsReadonlyOrLocked } from "@hooks/useIsReadonlyOrLocked";
import { useIsTransactionDateLocked } from "@hooks/useIsTransactionDateLocked";
import { AccrualMatchPreviewContent } from "@components/accounting/transactionMatch/AccrualMatchPreviewContent";
import { type MatchSummaryItem } from "@components/accounting/transactionMatch/aggregateMatches";
import { type CashDocumentMatch } from "@components/accounting/transactionMatch/cashDocumentMatches";
import { getTransferMatchPreview } from "@components/accounting/transactionMatch/transferMatchPreview";
import { suggestionToMatchableItem } from "@components/accounting/transactionMatch/mappers";
import { MatchableItem } from "@components/accounting/transactionMatch/types";
import {
    TransactionPreview,
    TransactionPreviewData,
    toTransactionPreviewData,
} from "../TransactionPreview/TransactionPreview";
import styles from "./TransferMatchCell.module.scss";

type TransferMatchCellTransaction = Pick<
    TransactionListItem,
    | "id"
    | "amount"
    | "entity"
    | "pairedTransfer"
    | "accrualMatches"
    | "transactionMatches"
    | "accrualMatchSuggestions"
    | "accrualAccountId"
>;

type TransferMatchCellValue = TransactionListItem["pairedTransfer"];

export interface TransferMatchSources {
    pairedTransfer?: TransactionListItem["pairedTransfer"];
    accrualMatches?: readonly MatchSummaryItem[] | null;
    transactionMatches?: readonly CashDocumentMatch[] | null;
    amount?: number;
}

interface TransferMatchCellContentProps {
    transaction: TransferMatchCellTransaction;
    isReadonlyOrLocked: boolean;
    isDateLocked?: boolean;
}

export function hasRenderableTransferMatchSources(
    transaction: TransferMatchSources | undefined,
): boolean {
    if (!transaction) {
        return false;
    }
    return getTransferMatchPreview(transaction).kind !== "empty";
}

export const TransferMatchCellContent = memo(
    ({
        transaction,
        isReadonlyOrLocked,
        isDateLocked = false,
    }: TransferMatchCellContentProps) => {
        const { openOverlay } = useOverlays();
        const { pairedTransfer } = transaction;

        const preview = useMemo(
            () => getTransferMatchPreview({ ...transaction, isDateLocked }),
            [transaction, isDateLocked],
        );

        const handleOpenMatch = useCallback(() => {
            if (isReadonlyOrLocked) {
                return;
            }
            if (preview.kind !== "suggestion") {
                openOverlay("transactionMatch", {
                    transactionId: transaction.id,
                });
                return;
            }
            const suggestedItems = preview.items
                .map((suggestion) =>
                    suggestionToMatchableItem(suggestion, transaction.entity),
                )
                .filter((item): item is MatchableItem => item != null);
            openOverlay("transactionMatch", {
                transactionId: transaction.id,
                suggestedItems:
                    suggestedItems.length > 0 ? suggestedItems : undefined,
            });
        }, [
            openOverlay,
            transaction.id,
            transaction.entity,
            isReadonlyOrLocked,
            preview,
        ]);

        const previewData: TransactionPreviewData | null = useMemo(
            () => toTransactionPreviewData(pairedTransfer),
            [pairedTransfer],
        );

        if (
            preview.kind === "accrualMatch" ||
            preview.kind === "suggestion" ||
            preview.kind === "cashDocument"
        ) {
            return (
                <AccrualMatchPreviewContent
                    accrualMatches={preview.items}
                    onClick={handleOpenMatch}
                    readonly={isReadonlyOrLocked}
                    isSuggested={preview.kind === "suggestion"}
                />
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.cell}>
                <EditableCellContent
                    className={classNames({
                        [styles.pairedTransfer]: pairedTransfer,
                    })}
                    onClick={handleOpenMatch}
                    isEmpty={!pairedTransfer}
                    readonly={isReadonlyOrLocked}
                >
                    {previewData && (
                        <TransactionPreview transaction={previewData} />
                    )}
                </EditableCellContent>
            </ViewTableCell>
        );
    },
);
TransferMatchCellContent.displayName = "TransferMatchCellContent";

export const TransferMatchCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        TransactionListItem,
        TransferMatchCellValue
    >) => {
        const transaction = row.original;
        const isReadonlyOrLocked = useIsReadonlyOrLocked(transaction, table);
        const isDateLocked = useIsTransactionDateLocked(transaction);

        return (
            <TransferMatchCellContent
                transaction={transaction}
                isReadonlyOrLocked={isReadonlyOrLocked}
                isDateLocked={isDateLocked}
            />
        );
    },
);
TransferMatchCell.displayName = "TransferMatchCell";
